import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import {
  BUSINESS_TYPES,
  type BusinessSettingsModel,
  type BusinessType,
  type ComplianceStatusModel
} from '../../../Models/ReceiptModel';
import { ReceiptService } from '../../../Services/ReceiptService';
import { routes } from '../../../Utils/routes';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { PremiumGate } from '../Premium/PremiumGate';
import { PremiumSubnav } from '../Premium/PremiumSubnav';
import './BusinessSettings.css';

/**
 * What goes at the top of a receipt.
 *
 * Changing anything here changes nothing about a receipt that has already been
 * issued: issuing freezes a snapshot of these fields onto the document. That
 * is the point of the snapshot, and it is worth saying on the screen, because
 * a tutor correcting a typo in their address reasonably expects it to apply
 * everywhere and it deliberately does not.
 */
export function BusinessSettings() {
  return (
    <PremiumGate feature={PremiumFeature.BusinessBranding}>
      <BusinessSettingsInner />
    </PremiumGate>
  );
}

function BusinessSettingsInner() {
  const { tr } = useTranslation();
  const [settings, setSettings] = useState<BusinessSettingsModel | null>(null);
  const [compliance, setCompliance] = useState<ComplianceStatusModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([ReceiptService.getSettings(), ReceiptService.compliance()])
      .then(([loaded, status]) => {
        // The address used to be one free-text line and is now street, house,
        // city and post code, because the Tax Authority export needs them
        // apart. A tutor who filled in the old field sees it in the street box
        // rather than an empty form, and saving moves it across.
        setSettings(loaded.address && !loaded.addressStreet && !loaded.addressCity
          ? { ...loaded, addressStreet: loaded.address }
          : loaded);
        setCompliance(status);
      })
      .catch(failure => setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR'))
      .finally(() => setLoading(false));
  }, []);

  function set<K extends keyof BusinessSettingsModel>(key: K, value: BusinessSettingsModel[K]) {
    setSettings(current => (current ? { ...current, [key]: value } : current));
    setSaved(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setBusy(true);
    setError(null);
    try {
      setSettings(await ReceiptService.saveSettings(settings));
      // Re-asked rather than guessed: which fields the law requires depends on
      // what was just saved, and that rule lives on the server.
      setCompliance(await ReceiptService.compliance());
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (!settings) return <ErrorState code={error ?? 'SERVER_ERROR'} />;

  // The next serial, spelled out. Numbering rules read as abstractions until
  // you see what they produce.
  //
  // Assembled exactly as allocateReceiptNumber does on the server: the prefix
  // runs straight into the year, and only the year carries a separator. A
  // preview that puts the hyphen somewhere else is worse than none, because it
  // is believed until the first receipt is issued.
  const yearPart = settings.yearPrefix ? `${new Date().getFullYear()}-` : '';
  const padded = String(settings.numberStart).padStart(Math.max(1, settings.numberLength), '0');
  const preview = `${settings.numberPrefix}${yearPart}${padded}`;

  return (
    <section className="business-settings">
      <PremiumSubnav />
      <h1 className="screen-title">{tr('businessTitle')}</h1>
      <p className="business-note">{tr('businessSnapshotNote')}</p>

      <form className="business-form" onSubmit={submit}>
        <section className="business-group">
          <h2>{tr('businessDetails')}</h2>
          <div className="business-grid">
            {/* What the business is decides the document it issues and whether
                VAT is carved out of the price, so it comes first. */}
            <label className="field">
              <span>{tr('businessTypeLabel')}</span>
              <select
                className="text-input"
                value={settings.businessType}
                onChange={event => set('businessType', event.target.value as BusinessType)}
              >
                {BUSINESS_TYPES.map(type => (
                  <option key={type} value={type}>{tr(BUSINESS_TYPE_KEYS[type])}</option>
                ))}
              </select>
            </label>
            <p className="business-help is-wide">{tr('businessTypeNote')}</p>

            <Field label={tr('businessName')} value={settings.businessName} onChange={value => set('businessName', value)} />
            <Field label={tr('businessNameEn')} value={settings.businessNameEn ?? null} onChange={value => set('businessNameEn', value)} />
            <Field
              label={tr('registrationNumber')}
              value={settings.registrationNumber}
              onChange={value => set('registrationNumber', value)}
              help={tr('registrationNumberHelp')}
            />
            <Field label={tr('addressStreet')} value={settings.addressStreet} onChange={value => set('addressStreet', value)} />
            <Field label={tr('addressHouse')} value={settings.addressHouse} onChange={value => set('addressHouse', value)} />
            <Field label={tr('addressCity')} value={settings.addressCity} onChange={value => set('addressCity', value)} />
            <Field label={tr('addressZip')} value={settings.addressZip} onChange={value => set('addressZip', value)} />
            <p className="business-help is-wide">{tr('addressPartsNote')}</p>
            <Field label={tr('businessAddressEn')} value={settings.addressEn ?? null} onChange={value => set('addressEn', value)} wide />
            <Field label={tr('phone')} value={settings.phone} onChange={value => set('phone', value)} />
            <Field label={tr('email')} value={settings.email} onChange={value => set('email', value)} />
            <Field label={tr('website')} value={settings.website} onChange={value => set('website', value)} wide />
            <Field
              label={tr('softwareRegistryNumber')}
              value={settings.softwareRegistryNumber}
              onChange={value => set('softwareRegistryNumber', value)}
              help={tr('softwareRegistryHelp')}
              wide
            />
          </div>
        </section>

        {compliance && (
          <section className="business-group">
            <h2>{tr('complianceTitle')}</h2>
            {compliance.ready ? (
              <p className="business-compliance is-ready">{tr('complianceReady')}</p>
            ) : (
              <div className="business-compliance is-missing">
                <p>{tr('complianceMissing')}</p>
                <ul>
                  {compliance.missing.map(field => (
                    <li key={field}>{tr(MISSING_FIELD_KEYS[field] ?? 'complianceFieldBusinessName')}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="business-help">
              {tr('complianceWillIssue', tr(compliance.documentType === 'tax_invoice_receipt'
                ? 'docTypeTaxInvoiceReceipt'
                : 'docTypeReceipt'))}
            </p>
            {compliance.vatRate > 0 && (
              <p className="business-help">
                <bdi>{tr('complianceVatRate', compliance.vatRate / 100)}</bdi>
              </p>
            )}
          </section>
        )}

        <section className="business-group">
          <h2>{tr('numberingTitle')}</h2>
          {settings.numberingLocked && (
            <p className="business-locked">{tr('numberingLockedNote')}</p>
          )}
          <fieldset className="business-fieldset" disabled={settings.numberingLocked}>
          <div className="business-grid">
            <Field label={tr('numberPrefix')} value={settings.numberPrefix} onChange={value => set('numberPrefix', value ?? '')} />
            <label className="field">
              <span>{tr('numberStart')}</span>
              <input
                className="text-input"
                inputMode="numeric"
                value={String(settings.numberStart)}
                onChange={event => set('numberStart', Number(event.target.value.replace(/\D/g, '')) || 1)}
              />
            </label>
            <label className="field">
              <span>{tr('numberLength')}</span>
              <input
                className="text-input"
                inputMode="numeric"
                value={String(settings.numberLength)}
                onChange={event => set('numberLength', Number(event.target.value.replace(/\D/g, '')) || 1)}
              />
            </label>
            <label className="check-label">
              <input type="checkbox" checked={settings.yearPrefix} onChange={event => set('yearPrefix', event.target.checked)} />
              <span>{tr('yearPrefix')}</span>
            </label>
            <label className="check-label">
              <input type="checkbox" checked={settings.perYearNumbering} onChange={event => set('perYearNumbering', event.target.checked)} />
              <span>{tr('perYearNumbering')}</span>
            </label>
            <p className="business-preview is-wide">
              {tr('nextNumberPreview')} <bdi>{preview}</bdi>
            </p>
          </div>
          </fieldset>
        </section>

        <OpenFormatExport />

        <section className="business-group">
          <h2>{tr('documentTitle')}</h2>
          <div className="business-grid">
            <Field label={tr('signatureName')} value={settings.signatureName} onChange={value => set('signatureName', value)} />
            <Field label={tr('signatureNameEn')} value={settings.signatureNameEn ?? null} onChange={value => set('signatureNameEn', value)} />
            <label className="field">
              <span>{tr('printSize')}</span>
              <select className="text-input" value={settings.printSize} onChange={event => set('printSize', event.target.value)}>
                <option value="a4">A4</option>
                <option value="a5">A5</option>
                <option value="receipt">{tr('printSizeReceipt')}</option>
              </select>
            </label>
            <label className="field is-wide">
              <span>{tr('footerText')}</span>
              <textarea
                className="text-input"
                rows={2}
                value={settings.footerText ?? ''}
                onChange={event => set('footerText', event.target.value || null)}
              />
            </label>
            <label className="field is-wide">
              <span>{tr('footerTextEn')}</span>
              <textarea
                className="text-input"
                dir="ltr"
                rows={2}
                value={settings.footerTextEn ?? ''}
                onChange={event => set('footerTextEn', event.target.value || null)}
              />
            </label>
          </div>
        </section>

        <div className="business-actions">
          <button type="submit" className="primary-button" disabled={busy}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
          {saved && <span className="business-saved">{tr('saved')}</span>}
          {error && <span className="business-error" role="alert">{tr('errorGeneric')}</span>}
        </div>
      </form>
    </section>
  );
}

function Field({ label, value, onChange, wide = false, help }: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  wide?: boolean;
  help?: string;
}) {
  return (
    <label className={wide ? 'field is-wide' : 'field'}>
      <span>{label}</span>
      {/* An emptied field is stored as null rather than an empty string, so a
          cleared value is absent on the document instead of a blank line. */}
      <input className="text-input" value={value ?? ''} onChange={event => onChange(event.target.value || null)} />
      {help && <small className="field-help">{help}</small>}
    </label>
  );
}

const BUSINESS_TYPE_KEYS: Record<BusinessType, TranslationKey> = {
  osek_patur: 'businessTypeOsekPatur',
  osek_murshe: 'businessTypeOsekMurshe',
  company: 'businessTypeCompany',
  amuta: 'businessTypeAmuta'
};

const MISSING_FIELD_KEYS: Record<string, TranslationKey> = {
  businessName: 'complianceFieldBusinessName',
  registrationNumber: 'complianceFieldRegistrationNumber',
  address: 'complianceFieldAddress',
  softwareRegistryNumber: 'complianceFieldSoftwareRegistryNumber'
};

/**
 * Where the export went.
 *
 * §2.1 of the uniform-structure instructions wants the module no more than
 * one step from the main menu, and inside this screen it was two. It has a
 * screen of its own now; this is the signpost for whoever looks here first.
 */
function OpenFormatExport() {
  const { tr } = useTranslation();
  return (
    <section className="business-group">
      <h2>{tr('openFormatTitle')}</h2>
      <p className="business-help">{tr('openFormatMoved')}</p>
      <Link className="ghost-button" to={routes.openFormat}>{tr('openFormatOpen')}</Link>
    </section>
  );
}
